/**
 * Browser surface host — mounts the surface the seam selected, and routes input.
 *
 * Extracted from `BrowserPreviewPanel.tsx`, where the surface components, the
 * input handlers and the status surface sat in the same 2,000-line file as the
 * panel's data plumbing. Nothing about which surface to mount could be read
 * without reading all of it.
 *
 * The host takes one `presentation` decision rather than the three booleans it
 * used to receive (`interactiveMode`, `nativeEmbeddedDisplayActive`,
 * `remoteWebRtcDisplayActive`), each derived in a different module. Same
 * behaviour, one source.
 *
 * @module BrowserSurfaceHost
 */
import type {
  BrowserCommand,
  BrowserCommentTarget,
  BrowserDisplaySession,
  BrowserSessionSnapshot,
  BrowserTabSnapshot,
  BrowserViewport,
  DesktopNativeBrowserResult,
  EnvironmentId,
  ScopedThreadRef,
  ThreadId,
} from "@cmmd-forge/contracts";
import type { BrowserPresentationDecision } from "@cmmd-forge/shared/browserPresentation";

import {
  browserSurfacePaintsCapturedFrame,
  resolveBrowserSurfaceMounts,
  type BrowserSurfaceMountPlan,
} from "./browserSurfaceMounts";
import { BrowserInteractiveCanvasSurface } from "./BrowserInteractiveCanvasSurface";
import { browserStatusCopy } from "./browserUnavailableCopy";
import { type ComponentProps, useEffect, useRef, type MutableRefObject } from "react";

import type { BrowserThreadState } from "~/browserStateStore";

import { remoteWebRtcBrowserSurfaceInstanceKey } from "../../components/BrowserPreviewPanel.logic";
import { BrowserCanvas } from "../../components/browser/BrowserCanvas";
import type { BrowserCommentDraft } from "../../components/browser/browserCommentDraftStore";
import { RemoteWebRtcBrowserSurface } from "../../components/browser/RemoteWebRtcBrowserSurface";
import { BrowserAgentCursorLayer } from "~/browser/AgentBrowserCursor";
import { useComposerDraftStore } from "~/composerDraftStore";
import { toastManager } from "~/components/ui/toast";
import { cropBrowserAnnotationScreenshot } from "~/components/browser/browserAnnotationCrop";
import { BrowserLauncher } from "../launcher/BrowserLauncher";
import { shouldShowBrowserLauncher } from "../launcher/browserLauncherLogic";
import type { BrowserLauncherViewModel } from "../launcher/useBrowserLauncher";
import {
  browserPreviewBrowserId,
  browserPreviewSessionId,
  browserPreviewTabId,
  browserPreviewTabUrl,
  browserPreviewUserId,
  type BrowserPreviewRuntimeState,
  type BrowserPreviewSessionState,
  type InteractivePointerPoint,
} from "./browserShellTypes";
import { BrowserPreviewState } from "./BrowserStatusSurface";
import {
  handleInteractiveKeyboardCommand,
  handleInteractivePointerCommand,
} from "./browserSurfaceInput";

type BrowserCanvasProps = ComponentProps<typeof BrowserCanvas>;

export interface BrowserSurfaceHostProps {
  readonly activeRenderMode: BrowserCanvasProps["renderMode"];
  readonly activeScreenshot: BrowserCanvasProps["screenshotDataUrl"];
  readonly activeScreenshotMeta: BrowserPreviewSessionState["activeScreenshotMeta"];
  readonly activeTab: BrowserTabSnapshot | null;
  readonly activeTabUrl: string | null;
  readonly browserDisplay: BrowserDisplaySession | undefined;
  readonly browserThreadState: BrowserThreadState | null;
  readonly commentDraft: BrowserCommentDraft;
  readonly displayFrame: BrowserPreviewSessionState["displayFrame"];
  readonly enqueueInteractiveBrowserCommand: (command: BrowserCommand) => void;
  readonly environmentId: EnvironmentId;
  readonly handleBrowserDisplayUpdate: (display: BrowserDisplaySession) => void;
  readonly handleCanvasTargetChange: BrowserCanvasProps["onTargetChange"];
  readonly handleContainerResize: NonNullable<BrowserCanvasProps["onContainerResize"]>;
  readonly handleNativeBrowserSurfaceError: (error: unknown) => void;
  readonly handleNativeBrowserSurfaceResult: (result: DesktopNativeBrowserResult) => void;
  readonly handleOpenExternal: (url: string) => void;
  readonly handleRemoteWebRtcSurfaceError: (error: unknown) => void;
  readonly handleSubmitComment: () => void | Promise<void>;
  readonly interactiveSurfaceViewport: BrowserViewport | undefined;
  readonly lastInteractivePointerPointRef: MutableRefObject<InteractivePointerPoint | null>;
  /**
   * What to offer when there is no page. Null keeps the plain status message —
   * which is what the failure and connecting states must always get.
   */
  readonly launcher: BrowserLauncherViewModel | null;
  /**
   * The one surface decision, from `@cmmd-forge/shared/browserPresentation`.
   *
   * Replaces the three booleans this host used to receive separately. They were
   * derived in three different modules and combined here, so no single place
   * stated what the user was supposed to be looking at.
   */
  readonly presentation: BrowserPresentationDecision;
  readonly previewVm: BrowserPreviewRuntimeState["previewVm"];
  readonly remoteWebRtcSurfaceViewport: BrowserViewport | undefined;
  readonly requestScreenshotCapture: (tabId: string) => void | Promise<void>;
  readonly session: BrowserSessionSnapshot | null;
  readonly shouldPreferBrowserCapture: boolean;
  readonly setCommentNote: (threadRef: ScopedThreadRef, note: string) => void;
  readonly clearCommentDraft: (threadRef: ScopedThreadRef) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}

export function BrowserSurfaceHost(props: BrowserSurfaceHostProps) {
  const plan = resolveBrowserSurfaceMounts({
    presentation: props.presentation,
    canRenderCanvas: props.previewVm.canRenderCanvas,
  });
  if (plan.shell === "interactive") {
    return <BrowserInteractivePreviewSurface {...props} plan={plan} />;
  }
  if (plan.shell === "annotation") {
    return <BrowserAnnotationPreviewSurface {...props} />;
  }
  return <BrowserPreviewStateForTab {...props} />;
}

function BrowserInteractivePreviewSurface(
  props: BrowserSurfaceHostProps & {
    readonly plan: Extract<BrowserSurfaceMountPlan, { shell: "interactive" }>;
  },
) {
  const tabId = browserPreviewTabId(props.activeTab);
  const browserId = browserPreviewBrowserId(props.session);
  const sessionId = browserPreviewSessionId(props.session);

  return (
    // Tagged with the tab it streams so client-side consumers that must tell
    // live surfaces apart (recording) can bind to the requested tab.
    <div className="relative h-full w-full" data-browser-tab-id={tabId ?? undefined}>
      <BrowserInteractiveCanvasSurface
        activeTabUrl={browserPreviewTabUrl(props.activeTab)}
        browserDisplay={props.browserDisplay}
        browserId={browserId}
        emptyLabel={props.previewVm.title}
        // The picture is gone; the input layer stays. `paintsCapturedFrame` is
        // false for every interactive plan, so this reads as `undefined` here and
        // the surface renders nothing of its own beneath the live transport.
        frameDataUrl={
          browserSurfacePaintsCapturedFrame(props.plan) ? props.displayFrame.dataUrl : undefined
        }
        interactionEnabled={props.plan.commandInput}
        nativeEmbeddedDisplayActive={props.plan.nativeOverlay}
        onContainerResize={props.handleContainerResize}
        onKeyboardCommand={(command) => handleInteractiveKeyboardCommand(props, command)}
        onNativeError={props.handleNativeBrowserSurfaceError}
        onNativeResult={props.handleNativeBrowserSurfaceResult}
        onPointerCommand={(command) => handleInteractivePointerCommand(props, command)}
        sessionId={sessionId}
        tabId={tabId}
        viewport={props.interactiveSurfaceViewport}
      />
      <BrowserRemoteWebRtcPreviewSurface
        active={props.plan.remoteVideo}
        browserDisplay={props.browserDisplay}
        browserId={browserId}
        browserThreadState={props.browserThreadState}
        environmentId={props.environmentId}
        onDisplayUpdated={props.handleBrowserDisplayUpdate}
        onError={props.handleRemoteWebRtcSurfaceError}
        sessionId={sessionId}
        tabId={tabId}
        threadId={props.threadId}
        userId={browserPreviewUserId(props.session)}
        viewport={props.remoteWebRtcSurfaceViewport}
      />
      <BrowserAgentCursorLayer threadRef={props.threadRef} tabId={tabId ?? null} />
    </div>
  );
}

interface BrowserRemoteWebRtcPreviewSurfaceProps {
  readonly active: boolean;
  readonly browserDisplay: BrowserDisplaySession | undefined;
  readonly browserId: ComponentProps<typeof RemoteWebRtcBrowserSurface>["browserId"];
  readonly browserThreadState: BrowserThreadState | null;
  readonly environmentId: EnvironmentId;
  readonly onDisplayUpdated: (display: BrowserDisplaySession) => void;
  readonly onError: (error: unknown) => void;
  readonly sessionId: ComponentProps<typeof RemoteWebRtcBrowserSurface>["sessionId"];
  readonly tabId: ComponentProps<typeof RemoteWebRtcBrowserSurface>["tabId"];
  readonly threadId: ThreadId;
  readonly userId: ComponentProps<typeof RemoteWebRtcBrowserSurface>["userId"];
  readonly viewport: BrowserViewport | undefined;
}

function BrowserRemoteWebRtcPreviewSurface(props: BrowserRemoteWebRtcPreviewSurfaceProps) {
  return (
    <RemoteWebRtcBrowserSurface
      key={remoteWebRtcBrowserSurfaceInstanceKey({
        browserId: props.browserId,
        sessionId: props.sessionId,
        viewport: props.viewport,
        display: props.browserDisplay,
      })}
      active={props.active}
      environmentId={props.environmentId}
      threadId={props.threadId}
      browserId={props.browserId}
      sessionId={props.sessionId}
      tabId={props.tabId}
      userId={props.userId}
      viewport={props.viewport}
      devicePixelRatio={props.browserDisplay?.devicePixelRatio}
      iceServers={props.browserDisplay?.iceServers}
      remoteIceCandidatesByClientId={props.browserThreadState?.displayIceCandidatesByClientId}
      onError={props.onError}
      onDisplayUpdated={props.onDisplayUpdated}
    />
  );
}

export interface BrowserElementPickAttachmentProps {
  readonly threadRef: ScopedThreadRef;
  readonly activeTabId: string | null;
  readonly activeTabUrl: string | null;
  readonly activeScreenshotMeta: BrowserPreviewSessionState["activeScreenshotMeta"];
  readonly screenshotDataUrl: BrowserCanvasProps["screenshotDataUrl"];
  readonly target: BrowserCommentTarget | null;
}

/**
 * Attaches the picked page area to the composer draft. Upstream's pick lands
 * its crop on the composer for the user to send; forge's annotation flow
 * dispatches its own turn, and the crop rides along as the pick's
 * attachment.
 */
export function BrowserElementPickAttachment(props: BrowserElementPickAttachmentProps) {
  const { threadRef, activeTabId, activeTabUrl, activeScreenshotMeta, screenshotDataUrl, target } =
    props;
  // The pick stays eligible until its attachment is DONE: a pick that lands
  // before the capture exists, or whose crop is cancelled by a capture
  // refresh, attaches when the next effect run sees the capture it needs.
  // Only a completed attach (or a pick with nothing croppable) consumes it.
  const attachedTargetRef = useRef<BrowserCommentTarget | null>(null);

  useEffect(() => {
    // The crop must come from the capture the pick was made on: a target
    // picked on another tab, or against another capture, would cut its rect
    // from the wrong page. An absent captureId on either side cannot disprove
    // the match, so only a known mismatch is rejected.
    if (
      !target ||
      target === attachedTargetRef.current ||
      !screenshotDataUrl ||
      target.tabId !== activeTabId ||
      (target.captureId != null &&
        activeScreenshotMeta?.captureId != null &&
        target.captureId !== activeScreenshotMeta.captureId)
    ) {
      return;
    }
    let cancelled = false;
    void cropBrowserAnnotationScreenshot({
      dataUrl: screenshotDataUrl,
      target,
      url: activeTabUrl,
      nowMs: Date.now(),
    })
      .then((attachment) => {
        // Cancelled means a newer capture superseded this crop; the target
        // stays eligible so the next run attaches against it.
        if (cancelled) return;
        if (attachment) {
          useComposerDraftStore.getState().addImage(threadRef, attachment);
          toastManager.add({
            type: "success",
            title: "Element attached",
            description: "The picked page area was added to the composer.",
          });
        }
        attachedTargetRef.current = target;
      })
      .catch(() => {
        if (cancelled) return;
        // Stays eligible: the next fresh capture retries the crop.
        toastManager.add({
          type: "error",
          title: "Could not attach the picked element",
          description: "The page area could not be cropped from the current capture.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [target, screenshotDataUrl, activeTabUrl, activeTabId, activeScreenshotMeta, threadRef]);

  return null;
}

function BrowserAnnotationPreviewSurface(props: BrowserSurfaceHostProps) {
  return (
    <>
      <BrowserElementPickAttachment
        threadRef={props.threadRef}
        activeTabId={props.activeTab?.tabId ?? null}
        activeTabUrl={props.activeTabUrl}
        activeScreenshotMeta={props.activeScreenshotMeta}
        screenshotDataUrl={props.activeScreenshot}
        target={props.commentDraft.target}
      />
      <BrowserCanvas
        url={props.activeTabUrl}
        tabId={props.activeTab?.tabId ?? null}
        renderMode={props.activeRenderMode}
        screenshotDataUrl={props.activeScreenshot}
        viewport={props.activeScreenshotMeta?.viewport}
        captureId={props.activeScreenshotMeta?.captureId}
        capturedAt={props.activeScreenshotMeta?.capturedAt}
        selectionEnabled={props.commentDraft.selecting}
        selectedTarget={props.commentDraft.target}
        onTargetChange={props.handleCanvasTargetChange}
        annotationNote={props.commentDraft.note}
        onAnnotationNoteChange={(note) => props.setCommentNote(props.threadRef, note)}
        onAnnotationSubmit={() => void props.handleSubmitComment()}
        onAnnotationCancel={() => props.clearCommentDraft(props.threadRef)}
        onContainerResize={props.handleContainerResize}
      />
    </>
  );
}

function BrowserPreviewStateForTab(props: BrowserSurfaceHostProps) {
  const activeTabId = props.activeTab?.tabId;
  if (props.launcher !== null && shouldShowBrowserLauncher(props.previewVm.status)) {
    return (
      <BrowserLauncher
        sections={props.launcher.sections}
        onOpenUrl={props.launcher.onOpenUrl}
        onRemoveRecent={props.launcher.onRemoveRecent}
        launchAction={props.launcher.launchAction}
      />
    );
  }
  // The presentation policy knows things `previewVm` cannot: `previewVm` reads
  // the display's own status, so a display that is perfectly healthy but cannot
  // be hosted by *this* client looks fine to it. Preferring the policy's reason
  // when it has one is what stops that case rendering as a blank idle pane.
  const copy = browserStatusCopy({
    ...(props.presentation.unavailableReason
      ? { reason: props.presentation.unavailableReason }
      : { reason: undefined }),
    title: props.previewVm.title,
    description: props.previewVm.description,
  });

  return (
    <BrowserPreviewState
      status={props.previewVm.status}
      title={copy.title}
      description={copy.description}
      canRefresh={Boolean(activeTabId && props.shouldPreferBrowserCapture)}
      canOpenExternal={Boolean(props.activeTabUrl)}
      {...(activeTabId
        ? { onRefresh: () => void props.requestScreenshotCapture(activeTabId) }
        : {})}
      {...(props.activeTabUrl
        ? { onOpenExternal: () => props.handleOpenExternal(props.activeTabUrl as string) }
        : {})}
    />
  );
}
